<template>
  <div class="bg p-3 pb-20 mb-auto h-full overflow-auto text-white" id="style-1">
    <!-- Global Loading Overlay -->
    <div
      v-if="loading"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
    </div>

    <div class="container mx-auto">
      <!-- หัวข้อและแถบค้นหา -->
      <div class="mb-6">
        <h1 class="text-2xl font-semibold text-indigo-300 mb-2">
          แอดมิน: ผู้เล่นเลเวล 25 / 26
        </h1>

        <p class="text-sm text-gray-400 mb-4">
          ตรวจสอบจาก collection
          <span class="text-indigo-300 font-semibold">characters</span>
          เท่านั้น ไม่อ่านข้อมูลจาก users และ camps แล้ว
        </p>

        <div class="flex flex-col sm:flex-row gap-4">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="ค้นหาด้วยชื่อตัวละครหรือ UID"
            class="flex-1 p-2 rounded-md bg-gray-700 text-indigo-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />

          <select
            v-model="itemMigrateFilter"
            class="p-2 rounded-md bg-gray-700 text-indigo-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">ไอเทม: ทั้งหมด</option>
            <option value="true">ไอเทม: ย้ายแล้ว</option>
            <option value="false">ไอเทม: ยังไม่ย้าย</option>
          </select>

          <select
            v-model="villagerMigrateFilter"
            class="p-2 rounded-md bg-gray-700 text-indigo-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">ชาวบ้าน: ทั้งหมด</option>
            <option value="true">ชาวบ้าน: ย้ายแล้ว</option>
            <option value="false">ชาวบ้าน: ยังไม่ย้าย</option>
          </select>

          <button
            @click="fetchCharacters"
            class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
          >
            <i class="fas fa-sync-alt mr-1"></i>
            โหลดใหม่
          </button>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="text-red-300 text-sm text-center mb-4">
        {{ error }}
      </div>

      <!-- Summary -->
      <div class="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div class="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div class="text-xs text-gray-400">ทั้งหมดที่โหลดมา</div>
          <div class="text-xl font-bold text-white">{{ characters.length }}</div>
        </div>

        <div class="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div class="text-xs text-gray-400">ตรงกับตัวกรอง</div>
          <div class="text-xl font-bold text-indigo-300">
            {{ filteredCharacters.length }}
          </div>
        </div>

        <div class="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div class="text-xs text-gray-400">ไอเทมยังไม่ย้าย</div>
          <div class="text-xl font-bold text-red-300">
            {{ notItemMigratedCount }}
          </div>
        </div>

        <div class="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div class="text-xs text-gray-400">ชาวบ้านยังไม่ย้าย</div>
          <div class="text-xl font-bold text-yellow-300">
            {{ notVillagerMigratedCount }}
          </div>
        </div>
      </div>

      <!-- Top Bulk Actions -->
      <div
        v-if="filteredCharacters.length > 0 && !batchModalOpen"
        class="mb-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-gray-900 border border-gray-700 rounded-lg p-4"
      >
        <div class="flex flex-wrap gap-3 items-center">
          <label class="flex items-center gap-2 text-sm text-indigo-200 cursor-pointer">
            <input
              type="checkbox"
              :checked="isAllPageSelected"
              @change="toggleSelectAllPage"
              class="rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500"
            />
            เลือกทั้งหมดในหน้านี้ ({{ paginatedCharacters.length }} คน)
          </label>

          <button
            v-if="selectedUids.length > 0"
            @click="selectedUids = []"
            class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
          >
            ล้างที่เลือก
          </button>
        </div>

        <button
          v-if="selectedUids.length > 0"
          @click="openBatchMigrateModal"
          class="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
        >
          <i class="fas fa-rocket mr-1"></i>
          Migrate ทั้งไอเทมและชาวบ้าน {{ selectedUids.length }} คน
        </button>
      </div>

      <!-- ตารางผู้เล่น -->
      <div class="overflow-x-auto bg-gray-800 rounded-md border border-gray-600">
        <table class="w-full text-left text-indigo-200">
          <thead class="bg-gray-900">
            <tr>
              <th class="p-3 text-sm font-semibold">
                <input
                  type="checkbox"
                  :checked="isAllPageSelected"
                  @change="toggleSelectAllPage"
                  class="rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500"
                />
              </th>
              <th class="p-3 text-sm font-semibold">#</th>
              <th class="p-3 text-sm font-semibold">ชื่อตัวละคร</th>
              <th class="p-3 text-sm font-semibold">เลเวล</th>
              <th class="p-3 text-sm font-semibold">ชื่อเสียง</th>
              <th class="p-3 text-sm font-semibold">UID</th>
              <th class="p-3 text-sm font-semibold text-center">ไอเทม V2</th>
              <th class="p-3 text-sm font-semibold text-center">ชาวบ้าน V2</th>
              <th class="p-3 text-sm font-semibold">การกระทำ</th>
            </tr>

            <tr class="bg-gray-950 text-xs text-gray-400">
              <th colspan="6"></th>
              <th class="p-2 text-center">characters.migratedToItemV2</th>
              <th class="p-2 text-center">characters.villagerSchemaVersion</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(char, index) in paginatedCharacters"
              :key="char.uid"
              class="border-b border-gray-700 hover:bg-gray-700 transition-colors duration-200"
            >
              <td class="p-3">
                <input
                  type="checkbox"
                  v-model="selectedUids"
                  :value="char.uid"
                  class="rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500"
                />
              </td>

              <td class="p-3 text-sm">
                {{ (currentPage - 1) * pageSize + index + 1 }}
              </td>

              <td class="p-3 text-sm">
                {{ char.characterName || 'N/A' }}
              </td>

              <td class="p-3 text-sm">
                {{ char.level }}
              </td>

              <td class="p-3 text-sm font-semibold text-yellow-300">
                {{ char.renown || 0 }}
              </td>

              <td class="p-3 text-sm font-mono text-xs">
                {{ char.uid }}
              </td>

              <td class="p-3 text-sm text-center">
                <span :class="char.migratedToItemV2 ? 'text-green-400' : 'text-red-400'">
                  {{ char.migratedToItemV2 ? '✅' : '❌' }}
                </span>
              </td>

              <td class="p-3 text-sm text-center">
                <span :class="char.villagerSchemaVersion === 2 ? 'text-green-400' : 'text-red-400'">
                  {{ char.villagerSchemaVersion === 2 ? '✅' : '❌' }}
                </span>
              </td>

              <td class="p-3 flex gap-2 flex-wrap">
                <button
                  class="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                  @click="openVerifyInNewTab(char.uid)"
                >
                  <i class="fas fa-search mr-1 text-xs"></i>
                  ตรวจสอบ
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Bottom Bulk Actions -->
      <div
        v-if="filteredCharacters.length > 0 && !batchModalOpen"
        class="mt-4 mb-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-gray-900 border border-indigo-700 rounded-lg p-4 sticky bottom-3 z-20 shadow-2xl"
      >
        <div class="flex flex-wrap gap-3 items-center">
          <label class="flex items-center gap-2 text-sm text-indigo-200 cursor-pointer">
            <input
              type="checkbox"
              :checked="isAllPageSelected"
              @change="toggleSelectAllPage"
              class="rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500"
            />
            เลือกทั้งหมดในหน้านี้ ({{ paginatedCharacters.length }} คน)
          </label>

          <div class="text-sm text-gray-400">
            เลือกแล้ว
            <span class="text-indigo-300 font-semibold">{{ selectedUids.length }}</span>
            คน
          </div>

          <button
            v-if="selectedUids.length > 0"
            @click="selectedUids = []"
            class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
          >
            ล้างที่เลือก
          </button>
        </div>

        <button
          v-if="selectedUids.length > 0"
          @click="openBatchMigrateModal"
          class="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-3 rounded-md text-sm font-semibold transition-colors shadow-lg"
        >
          <i class="fas fa-rocket mr-1"></i>
          Migrate ทั้งไอเทมและชาวบ้าน {{ selectedUids.length }} คน
        </button>

        <button
          v-else
          disabled
          class="bg-gray-700 text-gray-400 px-5 py-3 rounded-md text-sm font-semibold cursor-not-allowed"
        >
          เลือกผู้เล่นก่อน Migrate
        </button>
      </div>

      <!-- ถ้าไม่มีผู้เล่น -->
      <div
        v-if="!paginatedCharacters.length && !loading"
        class="text-center text-indigo-200 mt-6"
      >
        ไม่พบผู้เล่นที่ตรงกับเงื่อนไข
      </div>

      <!-- Pagination -->
      <div
        v-if="filteredCharacters.length > 0"
        class="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4"
      >
        <div class="text-sm text-gray-400">
          แสดง {{ startIndex + 1 }}–{{ endIndex }} จาก {{ filteredCharacters.length }} คน
          <span class="text-gray-500">(หน้า {{ currentPage }} / {{ totalPages }})</span>
        </div>

        <div class="flex gap-2">
          <button
            @click="currentPage--"
            :disabled="currentPage <= 1"
            class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-chevron-left mr-1"></i>
            ก่อนหน้า
          </button>

          <button
            @click="currentPage++"
            :disabled="currentPage >= totalPages"
            class="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ถัดไป
            <i class="fas fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Batch Migrate Progress -->
    <div
      v-if="batchModalOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90"
      style="padding: 1rem;"
    >
      <div
        class="bg-gray-800 border-2 border-indigo-500 rounded-xl p-6 max-w-2xl w-full mx-4 shadow-2xl flex flex-col overflow-hidden"
        style="max-height: 80vh;"
      >
        <!-- Header -->
        <div class="flex-shrink-0 text-center mb-4" style="flex-shrink: 0;">
          <i class="fas fa-rocket text-4xl text-indigo-400 mb-3"></i>

          <h2 class="text-xl font-bold text-white">
            Migrate ทั้งไอเทมและชาวบ้าน
          </h2>

          <p class="text-gray-300 text-sm mt-1">
            ส่งคำสั่งพร้อมกันครั้งละ 100 UID
          </p>

          <p class="text-gray-500 text-xs mt-1">
            ตรวจสอบสถานะจาก characters เท่านั้น
          </p>
        </div>

        <!-- Scrollable List -->
        <div class="flex-1 overflow-y-auto space-y-2 pr-1" style="min-height: 0;">
          <div
            v-for="entry in batchEntries"
            :key="entry.uid"
            class="bg-gray-700 rounded-lg p-3 flex items-center justify-between gap-3"
          >
            <div class="min-w-0">
              <div class="text-sm font-semibold text-white truncate">
                {{ entry.characterName || entry.uid }}
              </div>

              <div class="text-xs text-gray-400 font-mono truncate">
                {{ entry.uid }}
              </div>
            </div>

            <div class="text-right shrink-0">
              <div v-if="entry.status === 'pending'" class="text-xs text-gray-400">
                ⏳ รอดำเนินการ
              </div>

              <div v-else-if="entry.status === 'running'" class="text-xs text-yellow-400">
                🔄 กำลังดำเนินการ...
              </div>

              <div v-else-if="entry.status === 'done'" class="text-xs text-green-400">
                ✅ เสร็จสิ้น
              </div>

              <div v-else-if="entry.status === 'error'" class="text-xs text-red-400">
                ❌ {{ entry.error }}
              </div>

              <div v-if="entry.detail" class="text-xs text-gray-500 mt-1">
                {{ entry.detail }}
              </div>
            </div>
          </div>
        </div>

        <!-- Footer: Progress + Buttons -->
        <div class="flex-shrink-0 mt-4 pt-4 border-t border-gray-600" style="flex-shrink: 0;">
          <div class="bg-gray-700 rounded-lg p-3 mb-3 text-center text-sm">
            <span class="text-white font-semibold">{{ batchProgress }} / {{ batchTotal }}</span>
            <span class="text-gray-400"> ตัวละครเสร็จสิ้น</span>

            <div class="w-full bg-gray-600 rounded-full h-2 mt-2">
              <div
                class="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                :style="{ width: (batchTotal ? (batchProgress / batchTotal * 100) : 0) + '%' }"
              ></div>
            </div>
          </div>

          <div class="flex gap-3">
            <button
              @click="closeBatchModal"
              :disabled="batchRunning"
              class="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              ปิด
            </button>

            <button
              v-if="!batchStarted"
              @click="startBatchMigrate"
              class="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              <i class="fas fa-play mr-1"></i>
              เริ่ม Migrate
            </button>

            <button
              v-else-if="!batchRunning"
              @click="fetchCharacters"
              class="flex-1 bg-green-600 hover:bg-green-500 text-white py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              <i class="fas fa-sync-alt mr-1"></i>
              โหลดข้อมูลใหม่
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';

export default {
  name: 'AdminMigratePlayers',

  setup() {
    const characters = ref([]);
    const searchQuery = ref('');
    const itemMigrateFilter = ref('');
    const villagerMigrateFilter = ref('');
    const loading = ref(true);
    const error = ref('');

    // Pagination
    const currentPage = ref(1);
    const pageSize = ref(300);

    // Selection
    const selectedUids = ref([]);

    // Batch migrate modal
    const batchModalOpen = ref(false);
    const batchRunning = ref(false);
    const batchEntries = ref([]);
    const batchProgress = ref(0);
    const batchTotal = ref(0);
    const batchStarted = ref(false);

    const fetchCharacters = async () => {
      loading.value = true;
      error.value = '';

      try {
        const snapshot = await projectFirestore
          .collection('characters')
          .where('level', 'in', [30])
          .get();

        const charData = snapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        }));

        charData.sort((a, b) => (a.renown || 0) - (b.renown || 0));

        characters.value = charData;

        console.log('[AdminMigratePlayers] characters loaded docs:', snapshot.size);
      } catch (err) {
        console.error('[AdminMigratePlayers] fetchCharacters error:', err);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
      } finally {
        loading.value = false;
      }
    };

    const filteredCharacters = computed(() => {
      let filtered = characters.value;

      if (searchQuery.value.trim()) {
        const q = searchQuery.value.trim().toLowerCase();

        filtered = filtered.filter((c) => {
          const name = (c.characterName || '').toLowerCase();
          const uid = (c.uid || '').toLowerCase();

          return name.includes(q) || uid.includes(q);
        });
      }

      if (itemMigrateFilter.value !== '') {
        const target = itemMigrateFilter.value === 'true';

        filtered = filtered.filter((c) => {
          return !!c.migratedToItemV2 === target;
        });
      }

      if (villagerMigrateFilter.value !== '') {
        const target = villagerMigrateFilter.value === 'true';

        filtered = filtered.filter((c) => {
          return (c.villagerSchemaVersion === 2) === target;
        });
      }

      return filtered;
    });

    const notItemMigratedCount = computed(() => {
      return characters.value.filter((c) => !c.migratedToItemV2).length;
    });

    const notVillagerMigratedCount = computed(() => {
      return characters.value.filter((c) => c.villagerSchemaVersion !== 2).length;
    });

    const totalPages = computed(() => {
      return Math.max(1, Math.ceil(filteredCharacters.value.length / pageSize.value));
    });

    const startIndex = computed(() => {
      return (currentPage.value - 1) * pageSize.value;
    });

    const endIndex = computed(() => {
      return Math.min(startIndex.value + pageSize.value, filteredCharacters.value.length);
    });

    const paginatedCharacters = computed(() => {
      return filteredCharacters.value.slice(startIndex.value, endIndex.value);
    });

    const isAllPageSelected = computed(() => {
      if (paginatedCharacters.value.length === 0) return false;

      return paginatedCharacters.value.every((c) => {
        return selectedUids.value.includes(c.uid);
      });
    });

    watch([searchQuery, itemMigrateFilter, villagerMigrateFilter], () => {
      currentPage.value = 1;
      selectedUids.value = [];
    });

    watch(currentPage, () => {
      selectedUids.value = [];
    });

    watch(totalPages, (newTotalPages) => {
      if (currentPage.value > newTotalPages) {
        currentPage.value = newTotalPages;
      }
    });

    const toggleSelectAllPage = () => {
      const pageUids = paginatedCharacters.value.map((c) => c.uid);

      if (isAllPageSelected.value) {
        selectedUids.value = selectedUids.value.filter((uid) => {
          return !pageUids.includes(uid);
        });
      } else {
        selectedUids.value = [...new Set([...selectedUids.value, ...pageUids])];
      }
    };

    const openVerifyInNewTab = (userId) => {
      window.open(`/admin/migrate/${userId}`, '_blank');
    };

    const openBatchMigrateModal = () => {
      const targets = selectedUids.value
        .map((uid) => characters.value.find((c) => c.uid === uid))
        .filter(Boolean);

      if (targets.length === 0) {
        alert('กรุณาเลือกตัวละครอย่างน้อย 1 คน');
        return;
      }

      batchEntries.value = targets.map((char) => {
        const needItemMigrate = !char.migratedToItemV2;
        const needCampMigrate = char.villagerSchemaVersion !== 2;

        let detail = '';

        if (!needItemMigrate && !needCampMigrate) {
          detail = 'ไม่มีอะไรต้อง migrate';
        } else if (needItemMigrate && needCampMigrate) {
          detail = 'ต้อง migrate ไอเทมและชาวบ้าน';
        } else if (needItemMigrate) {
          detail = 'ต้อง migrate ไอเทม';
        } else if (needCampMigrate) {
          detail = 'ต้อง migrate ชาวบ้าน';
        }

        return {
          uid: char.uid,
          characterName: char.characterName,
          status: 'pending',
          detail,
          error: '',
        };
      });

      batchProgress.value = 0;
      batchTotal.value = targets.length;
      batchRunning.value = false;
      batchStarted.value = false;
      batchModalOpen.value = true;

      console.log('[openBatchMigrateModal] targets:', targets.length);
    };

    const closeBatchModal = () => {
      if (batchRunning.value) return;
      batchModalOpen.value = false;
    };

    const runMigrateForEntry = async (entry, gameActions) => {
      const char = characters.value.find((c) => c.uid === entry.uid);

      if (!char) {
        entry.status = 'error';
        entry.error = 'ไม่พบ character ในหน้าปัจจุบัน';
        return;
      }

      const needItemMigrate = !char.migratedToItemV2;
      const needCampMigrate = char.villagerSchemaVersion !== 2;

      if (!needItemMigrate && !needCampMigrate) {
        entry.status = 'done';
        entry.detail = 'ไม่มีอะไรต้อง migrate';
        return;
      }

      entry.status = 'running';
      entry.error = '';

      try {
        if (needItemMigrate) {
          entry.detail = 'กำลัง migrate ไอเทม...';

          await gameActions({
            action: 'adminMigratePlayer',
            targetUid: char.uid,
          });

          entry.detail = 'migrate ไอเทมสำเร็จ';
        }

        if (needCampMigrate) {
          entry.detail = needItemMigrate
            ? 'migrate ไอเทมสำเร็จ, กำลัง migrate ชาวบ้าน...'
            : 'กำลัง migrate ชาวบ้าน...';

          await gameActions({
            action: 'outpostActions',
            subAction: 'adminMigrateCampVillagers',
            targetUid: char.uid,
          });

          entry.detail = needItemMigrate
            ? 'migrate ไอเทมและชาวบ้านสำเร็จ'
            : 'migrate ชาวบ้านสำเร็จ';
        }

        entry.status = 'done';
      } catch (err) {
        console.error('[Batch migrate error]', char.uid, err);

        entry.status = 'error';
        entry.error =
          err?.message ||
          err?.details ||
          'เกิดข้อผิดพลาด';
      }
    };

    const startBatchMigrate = async () => {
      if (batchStarted.value || batchRunning.value) return;

      batchStarted.value = true;
      batchRunning.value = true;
      batchProgress.value = 0;

      const gameActions = projectFunctions.httpsCallable('gameActions');

      // ส่งพร้อมกันครั้งละ 100 UID
      const chunkSize = 100;

      try {
        for (let i = 0; i < batchEntries.value.length; i += chunkSize) {
          const chunk = batchEntries.value.slice(i, i + chunkSize);

          console.log(
            `[BatchMigrate] running chunk ${Math.floor(i / chunkSize) + 1}, size:`,
            chunk.length,
            chunk.map((entry) => entry.uid)
          );

          await Promise.all(
            chunk.map(async (entry) => {
              await runMigrateForEntry(entry, gameActions);
              batchProgress.value += 1;
            })
          );
        }
      } finally {
        batchRunning.value = false;

        console.log('[BatchMigrate] finished:', {
          total: batchTotal.value,
          done: batchEntries.value.filter((entry) => entry.status === 'done').length,
          error: batchEntries.value.filter((entry) => entry.status === 'error').length,
        });
      }
    };

    onMounted(() => {
      fetchCharacters();
    });

    return {
      characters,
      filteredCharacters,
      paginatedCharacters,
      searchQuery,
      itemMigrateFilter,
      villagerMigrateFilter,
      loading,
      error,
      currentPage,
      totalPages,
      startIndex,
      endIndex,
      pageSize,
      selectedUids,
      isAllPageSelected,
      batchModalOpen,
      batchRunning,
      batchEntries,
      batchProgress,
      batchTotal,
      batchStarted,
      notItemMigratedCount,
      notVillagerMigratedCount,
      fetchCharacters,
      toggleSelectAllPage,
      openVerifyInNewTab,
      openBatchMigrateModal,
      closeBatchModal,
      startBatchMigrate,
    };
  },
};
</script>

<style scoped>
.text-xxs {
  font-size: 0.65rem;
  line-height: 0.9rem;
}
</style>